import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, tokenStorage } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);      // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only probe /auth/me if we have a stored token — avoids unnecessary 401s for
    // public visitors and ensures Bearer-auth is sent with the probe.
    if (!tokenStorage.get()) {
      setUser(false);
      setLoading(false);
      return;
    }
    authApi.me()
      .then((res) => setUser(res.data))
      .catch(() => { tokenStorage.clear(); setUser(false); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    // Store token for Bearer auth (primary mechanism — survives any cookie issue).
    if (res.data?.token) tokenStorage.set(res.data.token);
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore network errors on logout */ }
    tokenStorage.clear();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
